import React, { useState } from 'react';

// Generate consistent color from name
const getColorFromName = (name) => {
  const colors = [
    { bg: 'bg-rose-500', text: 'text-white' },
    { bg: 'bg-orange-500', text: 'text-white' },
    { bg: 'bg-amber-500', text: 'text-white' },
    { bg: 'bg-emerald-500', text: 'text-white' },
    { bg: 'bg-teal-500', text: 'text-white' },
    { bg: 'bg-cyan-500', text: 'text-white' },
    { bg: 'bg-blue-500', text: 'text-white' },
    { bg: 'bg-indigo-500', text: 'text-white' },
    { bg: 'bg-violet-500', text: 'text-white' },
    { bg: 'bg-purple-500', text: 'text-white' },
    { bg: 'bg-pink-500', text: 'text-white' },
    { bg: 'bg-slate-600', text: 'text-white' },
  ];
  
  if (!name) return colors[0];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

// Get initials from name
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Size configurations
const sizeConfig = {
  xs: { container: 'w-6 h-6', text: 'text-[10px]' },
  sm: { container: 'w-8 h-8', text: 'text-xs' },
  md: { container: 'w-10 h-10', text: 'text-sm' },
  lg: { container: 'w-12 h-12', text: 'text-base' },
  xl: { container: 'w-16 h-16', text: 'text-lg' },
  '2xl': { container: 'w-20 h-20', text: 'text-xl' },
  '3xl': { container: 'w-24 h-24', text: 'text-2xl' },
  profile: { container: 'w-[100px] h-[100px]', text: 'text-3xl' },
};

/**
 * Avatar component with image support and initials fallback
 * 
 * @param {string} src - Image URL
 * @param {string} name - User name (for initials and color)
 * @param {string} size - xs | sm | md | lg | xl | 2xl | 3xl | profile
 * @param {string} className - Additional classes
 * @param {function} onClick - Click handler
 * @param {boolean} showBorder - Show white border
 */
const Avatar = ({ 
  src, 
  name = '', 
  size = 'md', 
  className = '', 
  onClick,
  showBorder = false 
}) => {
  const [imgError, setImgError] = useState(false);
  const color = getColorFromName(name);
  const initials = getInitials(name);
  const config = sizeConfig[size] || sizeConfig.md;
  
  const showImage = src && !imgError;
  
  const baseClasses = `
    ${config.container} 
    rounded-full 
    flex items-center justify-center 
    overflow-hidden 
    flex-shrink-0
    ${showBorder ? 'ring-2 ring-white shadow-md' : ''}
    ${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}
    ${className}
  `;
  
  if (showImage) {
    return (
      <div className={baseClasses} onClick={onClick}>
        <img
          src={src}
          alt={name}
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
          loading="lazy"
        />
      </div>
    );
  }
  
  // Initials fallback
  return (
    <div 
      className={`${baseClasses} ${color.bg} ${color.text}`}
      onClick={onClick}
    >
      <span className={`font-semibold ${config.text}`}>{initials}</span>
    </div>
  );
};

export default Avatar;
